import { findAncestorWithComponent } from "../utils/scene-graph";
AFRAME.registerComponent("artwork-frame", {
  schema: {
    model: { type: "string" },
    completed: { default: false }
  },
  init() {
    this.modelLoaded = false;
    this.el.addEventListener("model-loaded", this.onModelLoaded.bind(this));
    // Hide the frame initially until dimensions are ready
    this.el.object3D.visible = false;
  },
  update(oldData) {
    if (this.data.model !== oldData.model) {
      this.modelLoaded = false;
      // Hide frame while new model is loading
      this.el.object3D.visible = false;
      this.el.removeAttribute("gltf-model-plus");

      if (this.data.model) {
        this.el.setAttribute("gltf-model-plus", { src: this.data.model });
        this.el.setAttribute("id", "frame");
        this.el.emit("load-start");
      }
    }
  },
  onModelLoaded() {
    this.waitForValidDimensionsAndUpdate();
  },
  waitForValidDimensionsAndUpdate() {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max (50 * 100ms) - increased for animations
    let lastDimensions = null;
    let stableCount = 0;
    const requiredStableChecks = 5; // Dimensions must be stable for 5 checks (500ms) - more stable

    const checkDimensions = () => {
      attempts++;
      const dimensions = this.getArtworkDimensions();

      if (dimensions && dimensions.width > 0.01 && dimensions.height > 0.01) {
        // Check if dimensions are stable (unchanged from last check)
        if (lastDimensions) {
          const widthDiff = Math.abs(dimensions.width - lastDimensions.width);
          const heightDiff = Math.abs(dimensions.height - lastDimensions.height);
          const tolerance = 0.001; // 1mm tolerance

          if (widthDiff < tolerance && heightDiff < tolerance) {
            stableCount++;

            if (stableCount >= requiredStableChecks) {
              this.updateSizeAndPosition(dimensions.width, dimensions.height);
              return;
            }
          } else {
            stableCount = 0;
          }
        }

        lastDimensions = { ...dimensions };

        if (attempts < maxAttempts) {
          setTimeout(checkDimensions, 100);
        } else {
          console.warn("[Frame] Max attempts reached, applying frame with current dimensions:", dimensions);
          this.updateSizeAndPosition(dimensions.width, dimensions.height);
        }
      } else if (attempts < maxAttempts) {
        // Retry after a short delay
        stableCount = 0;
        lastDimensions = null;
        setTimeout(checkDimensions, 100);
      } else {
        console.error("[Frame] Max attempts reached, frame may not display correctly");
      }
    };

    checkDimensions();
  },
  getArtworkDimensions() {
    const artwork = findAncestorWithComponent(this.el, "artwork");
    if (!artwork) return null;

    // Find the medium entity (child with artwork-medium component)
    let mediumEntity = null;
    const children = artwork.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-medium"]) {
        mediumEntity = children[i];
        break;
      }
    }

    // If no medium, fall back to artwork mesh (for backward compatibility)
    const artworkMesh = mediumEntity ? mediumEntity.object3DMap.mesh : artwork.object3DMap.mesh;
    if (!artworkMesh) return null;

    // Get the entity's object3D scale (where entity-level scaling is applied)
    const entityScale = mediumEntity ? mediumEntity.object3D.scale : { x: 1, y: 1, z: 1 };

    // Get dimensions from the geometry's bounding box (local space, not world space)
    let width = 0;
    let height = 0;

    artworkMesh.traverse(child => {
      if (child.isMesh && child.geometry) {
        // Ensure the bounding box is computed
        if (!child.geometry.boundingBox) {
          child.geometry.computeBoundingBox();
        }

        const bbox = child.geometry.boundingBox;
        if (bbox) {
          // Get dimensions from geometry (local space)
          const bboxWidth = bbox.max.x - bbox.min.x;
          const bboxHeight = bbox.max.y - bbox.min.y;
          // Multiply by child mesh scale and entity scale
          const localWidth = bboxWidth * child.scale.x * entityScale.x;
          const localHeight = bboxHeight * child.scale.y * entityScale.y;

          // Use the largest dimensions found
          width = Math.max(width, localWidth);
          height = Math.max(height, localHeight);
        }
      }
    });

    return { width, height };
  },
  updateSizeAndPosition(width, height) {
    const model = this.el.getObject3D("mesh");

    if (!model) return;

    const offsetX = width * 0.5;
    const offsetY = height * 0.5;
    const cornerSize = 2.935;

    const target = new THREE.Vector3();

    model.traverse(node => {
      if (!node.isMesh) {
        return;
      }

      const boundingBox = node.geometry.boundingBox.getSize(target);

      switch (node.name) {
        // Top left corner
        case "corner_top_left":
          node.position.set(-offsetX, offsetY, 0);
          node.visible = true;
          break;
        // Top right corner
        case "corner_top_right":
          node.position.set(offsetX, offsetY, 0);
          node.visible = true;
          break;
        // Bottom left corner
        case "corner_bottom_left":
          node.position.set(-offsetX, -offsetY, 0);
          node.visible = true;
          break;
        // Bottom right corner
        case "corner_bottom_right":
          node.position.set(offsetX, -offsetY, 0);
          node.visible = true;
          break;
        // Top
        case "center_top":
          node.position.set(0, offsetY, 0);
          node.scale.x = width / boundingBox.x - cornerSize;
          node.visible = true;
          break;
        // Left
        case "center_left":
          node.position.set(-offsetX, 0, 0);
          node.scale.y = height / boundingBox.y - cornerSize;
          node.visible = true;
          break;
        // Bottom
        case "center_bottom":
          node.position.set(0, -offsetY, 0);
          node.scale.x = width / boundingBox.x - cornerSize;
          node.visible = true;
          break;
        // Right
        case "center_right":
          node.position.set(offsetX, 0, 0);
          node.scale.y = height / boundingBox.y - cornerSize;
          node.visible = true;
          break;
      }

      // Duplicate the material because it's shared between the frame pieces
      // Duplication of texture has been removed because it cause issues in Metaverse GLTFLoader
      const material = node.material.clone();
      const texture = material.map;
      texture.anisotropy = 8;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(node.scale.x, node.scale.y);

      // Flip texture repeat values for vertical pieces
      if (["center_left", "center_right"].includes(node.name)) {
        texture.repeat.set(node.scale.y, node.scale.x);
      }

      texture.needsUpdate = true;
      material.map = texture;
      material.toneMapped = false;
      node.material = material;
    });
    model.position.set(0, 0, 0.008);

    // Show the frame now that it's properly sized
    this.el.object3D.visible = true;

    this.updateQrCode();
  },
  updateQrCode() {
    const artwork = findAncestorWithComponent(this.el, "artwork");
    const artworkComponent = artwork.components["artwork"].data;

    // Find artwork-qr among parent's children (siblings)
    let artworkQr = null;
    const siblings = this.el.parentEl.children || [];
    for (let i = 0; i < siblings.length; i++) {
      if (siblings[i].components && siblings[i].components["artwork-qr"]) {
        artworkQr = siblings[i];
        break;
      }
    }

    if (artworkQr) {
      const artworkQrData = {
        frame: this.data.model,
        glbModel: artworkComponent.glbModel,
        remoteArtwork: artworkComponent.remoteArtwork,
        image: artworkComponent.image,
        completed: this.data.completed
      };
      artworkQr.setAttribute("artwork-qr", artworkQrData);
    }
  }
});
